import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const testimonials = [
  {
    quote:
      '过去我们做EAP，年报使用率不到4%，员工觉得「那是给有问题的人用的」。引入有心AI引擎三个月后，主动使用率达到67%——因为员工感知到的是「帮我写更好回复」的沟通助手，不是心理测评。',
    author: '李薇',
    title: '某互联网科技公司 HRD',
  },
  {
    quote:
      '我们销售团队压力大、turnover高。销售13周桌游工作坊那次，一个平时最抗拒「心理活动」的老销售，在角色扮演环节突然红了眼眶——他说终于有人懂他跟客户周旋时的憋屈了。',
    author: '王建国',
    title: '某快消品企业 销售总监',
  },
  {
    quote:
      '公司并购整合期，两个团队的融合让我夜不能寐。重塑桌游工作坊让两边的人在游戏规则中放下了防备，AI引擎那段时间频繁预警沟通摩擦热点，帮我们提前化解了至少三次可能升级的冲突。',
    author: '陈明远',
    title: '某制造业集团 VP',
  },
];

const processSteps = [
  { step: '01', title: '需求沟通', desc: '1-2天深度沟通，输出《需求洞察简报》' },
  { step: '02', title: 'AI引擎试运行', desc: '2-4周技术对接，收集基线数据' },
  { step: '03', title: '桌游体验', desc: '半天沉浸式桌游诊断工作坊' },
  { step: '04', title: '定制方案', desc: '1周内出具《效能提升方案》' },
  { step: '05', title: '全面交付', desc: '按方案周期启动服务交付' },
  { step: '06', title: '持续追踪', desc: 'AI持续追踪+季度复盘优化' },
];

export default function EpilogueSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const testimonialRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const heading = headingRef.current;
    if (!heading) return;

    // Wait for fonts before splitting
    document.fonts.ready.then(() => {
      import('split-type').then(({ default: SplitType }) => {
        const split = new SplitType(heading, { types: 'words,chars' });

        if (split.chars && split.chars.length > 0) {
          gsap.set(heading, { perspective: 400 });
          gsap.from(split.chars, {
            y: 50,
            opacity: 0,
            duration: 1.2,
            ease: 'power4.out',
            stagger: { amount: 0.8, from: 'start' },
            scrollTrigger: {
              trigger: heading,
              start: 'top bottom-=10%',
              toggleActions: 'play none none reverse',
            },
          });
        }
      });
    });
  }, []);

  useEffect(() => {
    testimonialRefs.current.forEach((el) => {
      if (!el) return;
      gsap.from(el, {
        y: 40,
        opacity: 0,
        duration: 1,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: el,
          start: 'top bottom-=10%',
          toggleActions: 'play none none reverse',
        },
      });
    });
  }, []);

  return (
    <section
      id="epilogue"
      ref={sectionRef}
      className="relative"
      style={{
        backgroundColor: '#fcfcfc',
        padding: '10vmax 5vmax',
      }}
    >
      <div
        className="absolute top-0 left-0 right-0 h-px"
        style={{ backgroundColor: 'rgba(21, 20, 20, 0.08)' }}
      />

      <div className="max-w-6xl mx-auto">
        {/* Staggered Heading */}
        <h2
          ref={headingRef}
          className="font-body mb-16"
          style={{
            fontSize: 'clamp(1.8rem, 4.5vw, 3rem)',
            fontWeight: 600,
            lineHeight: 1.35,
            letterSpacing: '0.02em',
            color: '#9a7a5a',
            textShadow: '1px 1px 0 rgba(255,255,255,0.5), 2px 2px 0 rgba(180,160,130,0.15)',
            maxWidth: '700px',
            wordBreak: 'keep-all',
          }}
        >
          <span
            style={{
              display: 'block',
              textShadow: '-1px -1px 0 rgba(255,255,255,0.7), 1px 1px 0 #d4c4b0, 2px 2px 0 rgba(180,160,130,0.35), 3px 3px 0 rgba(180,160,130,0.2), 4px 5px 10px rgba(180,160,130,0.15)',
            }}
          >
            同样的投入
          </span>
          <span
            style={{
              display: 'block',
              textShadow: '-1px -1px 0 rgba(255,255,255,0.7), 1px 1px 0 #d4c4b0, 2px 2px 0 rgba(180,160,130,0.35), 3px 3px 0 rgba(180,160,130,0.2), 4px 5px 10px rgba(180,160,130,0.15)',
            }}
          >
            更早的看见，更顺的改变
          </span>
        </h2>

        {/* Testimonials */}
        <div className="mb-24">
          <p className="text-caption text-umber mb-8">客户证言</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
            {testimonials.map((t, i) => (
              <div
                key={i}
                ref={(el) => { testimonialRefs.current[i] = el; }}
                className="p-6 lg:p-8"
                style={{
                  borderLeft: i > 0 ? '1px solid rgba(21, 20, 20, 0.08)' : 'none',
                }}
              >
                <p
                  className="mb-6"
                  style={{
                    fontSize: '0.92rem',
                    lineHeight: 1.8,
                    color: '#151414',
                    fontFamily: "'Noto Serif SC', serif",
                  }}
                >
                  「{t.quote}」
                </p>
                <div>
                  <p
                    className="font-display"
                    style={{
                      fontSize: '0.92rem',
                      color: '#151414',
                    }}
                  >
                    {t.author}
                  </p>
                  <p
                    className="text-caption"
                    style={{ color: '#6e5e50' }}
                  >
                    {t.title}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Process */}
        <div>
          <p className="text-caption text-umber mb-8">合作流程</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-0">
            {processSteps.map((step, i) => (
              <div
                key={i}
                className="relative p-4"
                style={{
                  borderLeft: i > 0 ? '1px solid rgba(21, 20, 20, 0.08)' : 'none',
                  borderTop: '1px solid rgba(21, 20, 20, 0.08)',
                }}
              >
                <span
                  className="font-display block mb-3"
                  style={{
                    fontSize: '2rem',
                    fontWeight: 300,
                    lineHeight: 1,
                    color: 'rgba(21, 20, 20, 0.08)',
                  }}
                >
                  {step.step}
                </span>
                <p
                  className="font-display mb-2"
                  style={{
                    fontSize: '0.85rem',
                    color: '#151414',
                  }}
                >
                  {step.title}
                </p>
                <p
                  style={{
                    fontSize: '0.78rem',
                    lineHeight: 1.5,
                    color: '#6e5e50',
                  }}
                >
                  {step.desc}
                </p>
              </div>
            ))}
          </div>

          <div
            className="h-px mt-0"
            style={{ backgroundColor: 'rgba(21, 20, 20, 0.08)' }}
          />
        </div>

        {/* CTA */}
        <div className="mt-24 text-center">
          {/* Subtle warm dot accent */}
          <div
            className="mx-auto mb-6"
            style={{
              width: '4px',
              height: '4px',
              borderRadius: '50%',
              backgroundColor: 'rgba(200, 170, 130, 0.3)',
            }}
          />
          <p
            className="font-serif mb-6"
            style={{
              fontSize: 'clamp(1.25rem, 2.5vw, 2rem)',
              fontWeight: 400,
              lineHeight: 1.3,
              color: '#8a7a68',
              textShadow: '1px 1px 0 rgba(255,255,255,0.5)',
            }}
          >
            每一个微小的沟通改善，都在为组织积蓄不可忽视的能量
          </p>
          {/* Whisper note */}
          <p
            className="mb-8"
            style={{
              fontSize: '0.92rem',
              color: 'rgba(160, 142, 120, 0.35)',
              fontFamily: "'Noto Serif SC', serif",
              fontStyle: 'italic',
              lineHeight: 1.6,
            }}
          >
            <span style={{ display: 'block', textShadow: '1px 1px 0 rgba(0,0,0,0.3), 2px 2px 0 rgba(0,0,0,0.15)' }}>
              有效的投入
            </span>
            <span style={{ display: 'block', textShadow: '1px 1px 0 rgba(0,0,0,0.3), 2px 2px 0 rgba(0,0,0,0.15)' }}>
              更早的看见，更顺的改变
            </span>
          </p>
          <button
            onClick={() => {
              const el = document.getElementById('custom');
              if (el) el.scrollIntoView({ behavior: 'smooth' });
            }}
            className="interactive px-8 py-4 text-caption transition-all duration-300 hover:scale-105"
            style={{
              backgroundColor: '#151414',
              color: '#fcfcfc',
              borderRadius: '9999px',
            }}
          >
            开始定制你的方案
          </button>
        </div>
      </div>

      <div
        className="absolute bottom-0 left-0 right-0 h-px"
        style={{ backgroundColor: 'rgba(21, 20, 20, 0.08)' }}
      />
    </section>
  );
}
